import React, { FC } from 'react';
import classNames from 'classnames';
import { AxiosPromise } from 'axios';

import {
  GetPaymentsSummaryByTermNumberResponse,
  PaymentsAndBillingInfoType,
} from '@clearcover/core-app-types';
import { env } from 'Shared/constants/env';
import {
  ConfirmationModal,
  FormModalHeader,
  useToast,
} from 'Shared/components';
import {
  Asset,
  Button,
  Checkbox,
  FlexGrid,
  FlexGridColumn,
  Modal,
  ModalFooter,
  PlusIcon,
} from '@clearcover/atlas';
import { paymentIcon } from 'Shared/constants/paymentIcon';
import styles from './styles.module.css';
import { useLazyApi } from 'Shared/services/Api';
import { usePolicyContext, usePolicyParams } from 'Policies/utils';
import {
  manageAutopayPolicy,
  managePaymentInformationPolicy,
} from 'Admin/authPolicies';
import { AddPaymentMethodForm } from '../../AddPaymentMethodForm';
import { EditAutoPaySchedule } from './EditAutoPaySchedule';
import { PaymentFrequencySection } from '../PaymentFrequencySection';
import { getPaymentMethodIconType } from '../utils';
import { formatDateShort, formatNumberToOrdinal } from 'Shared/utils';
import { DateTime } from 'luxon';

export type EditBillingInfoType = {
  billingInfo?: PaymentsAndBillingInfoType;
  bindDate?: string;
  isRenewalDraft: boolean;
  loading: boolean;
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
};

export const EditBillingInfo: FC<EditBillingInfoType> = ({
  billingInfo,
  bindDate,
  isRenewalDraft,
  fetchBillingData,
  loading,
}) => {
  const { toast } = useToast();
  const { policyId } = usePolicyParams();
  const {
    resourceEffectiveGrants,
    systemOfRecord,
    flags: { mandatedDownPay },
  } = usePolicyContext();

  const canEditPaymentInfo =
    systemOfRecord === 'clearcover' &&
    managePaymentInformationPolicy(resourceEffectiveGrants);

  const [{ loading: settingDefaultPaymentMethod }, setDefaultPaymentMethod] =
    useLazyApi({
      url: 'payments/setDefaultPaymentMethod',
      method: 'POST',
    });
  const [{ loading: removingPayment }, removePaymentMethod] = useLazyApi({
    url: 'payments/removePaymentMethod',
    method: 'POST',
  });
  const [{ loading: updatingAutoPay }, setAutoPay] = useLazyApi({
    url: 'payments/setAutoPay',
    method: 'POST',
  });

  const {
    defaultPaymentMethod = {
      id: '',
      type: 'bank',
      lastFour: '1234',
    },
    paymentMethods = [],
    hasAutoPay = false,
    accountId = '',
    invoiceId = '',

    paymentDueDate = '',
    autoPayEligibility = {
      eligible: false,
      reasons: [],
    },
    allowAutoPayDraftUpdate = false,
    autoPayDraftDay = 0,
  } = billingInfo || {};

  const paymentMethodsWithoutSweep = paymentMethods.filter(
    (paymentMethod) => paymentMethod.paymentProcessor !== 'SWEEP'
  );
  const isDefaultPaymentMethodSweep =
    defaultPaymentMethod.paymentProcessor === 'SWEEP';
  const autoPayDisabledTooltip = isDefaultPaymentMethodSweep
    ? 'There must be a personal method on file to enable autopay'
    : undefined;

  const allowedToUpdateAutoPay = manageAutopayPolicy(resourceEffectiveGrants);
  const backupAutoPayDayPhrase = paymentDueDate
    ? `Autopay on the ${formatNumberToOrdinal(
        DateTime.fromISO(paymentDueDate).day
      )} of each month`
    : '';

  return (
    <>
      <FormModalHeader>
        {env.paymentsAndBillingNewDesign
          ? 'Edit Payment Settings'
          : 'Edit Billing Information'}
      </FormModalHeader>
      <FlexGrid
        className={styles.container}
        data-testid={'edit-billing-info-modal'}
      >
        <FlexGridColumn mobile={8} mobileOffset={2}>
          <h2>Billing Information</h2>
          <h5>
            {env.paymentsAndBillingNewDesign
              ? 'Manage payment methods'
              : 'Payment Method'}
          </h5>
          {paymentMethodsWithoutSweep.length === 0 && (
            <p>No personal payment methods on file.</p>
          )}
          {paymentMethodsWithoutSweep.map((paymentMethod) => {
            const isDefault = paymentMethod.id === defaultPaymentMethod.id;

            const paymentMethodIconType =
              getPaymentMethodIconType(paymentMethod);
            return (
              <div
                className={classNames('fs-exclude', styles.paymentMethod)}
                key={paymentMethod.id}
              >
                <div className={styles.paymentInfo}>
                  {paymentMethodIconType && (
                    <Asset
                      icon={paymentIcon[paymentMethodIconType]}
                      description={'credit card'}
                      height={38}
                      width={38}
                    />
                  )}
                  <div className={styles.creditCardInfo}>
                    <div>
                      {paymentMethod.lastFour
                        ? `•••• •••• •••• ${paymentMethod.lastFour}`
                        : 'None'}
                    </div>
                    {paymentMethod.type === 'creditCard' && (
                      <div className={styles.expires}>
                        Expires: {paymentMethod.expirationMonth}/
                        {paymentMethod.expirationYear}
                      </div>
                    )}
                  </div>
                  {isDefault && <span className={styles.badge}>Default</span>}
                </div>

                <div className={styles.buttonGroup}>
                  <Button
                    data-testid={`set-payment-method-${paymentMethod.id}`}
                    title={
                      isDefault
                        ? 'Unable to set default payment method'
                        : 'Set as default payment method'
                    }
                    size="sm"
                    variant={'secondary'}
                    className={styles.defaultButton}
                    disabled={
                      isDefault || loading || updatingAutoPay || removingPayment
                    }
                    isSubmitting={settingDefaultPaymentMethod}
                    onClick={() => {
                      setDefaultPaymentMethod({
                        data: {
                          invoiceId,
                          paymentMethodId: paymentMethod.id,
                        },
                      })
                        .then(() => {
                          fetchBillingData().then(() => {
                            toast({
                              message: 'Default Payment Method has been set',
                            });
                          });
                        })
                        .catch((error) =>
                          toast({
                            message: error.message,
                            type: 'error',
                          })
                        );
                    }}
                  >
                    Make Default
                  </Button>
                  <Modal
                    toggle={(openModal) => (
                      <Button
                        data-testid={`remove-payment-method-${paymentMethod.id}`}
                        title={(() => {
                          if (isDefault)
                            return 'Unable to remove default payment methods';

                          return 'Remove payment method';
                        })()}
                        size="sm"
                        variant="danger-secondary"
                        className={styles.removeButton}
                        disabled={
                          isDefault ||
                          loading ||
                          updatingAutoPay ||
                          settingDefaultPaymentMethod
                        }
                        isSubmitting={removingPayment}
                        onClick={openModal}
                      >
                        Remove
                      </Button>
                    )}
                  >
                    {(close) => (
                      <ConfirmationModal
                        close={close}
                        isSubmitting={removingPayment}
                        headerText={'Confirm payment method removal'}
                        bodyText={`You are about to remove payment method ending with ${paymentMethod.lastFour}. Please confirm that you would like to remove this payment method.`}
                        onSubmit={() => {
                          removePaymentMethod({
                            data: {
                              accountId: accountId,
                              paymentMethodId: paymentMethod.id,
                            },
                          })
                            .then(() => {
                              fetchBillingData().then(() => {
                                toast({
                                  message: 'Payment Method has been removed',
                                });
                                close();
                              });
                            })
                            .catch((error) => {
                              toast({
                                message: error.message,
                                type: 'error',
                              });
                            });
                        }}
                      />
                    )}
                  </Modal>
                </div>
              </div>
            );
          })}
          {isRenewalDraft && !env.paymentsAndBillingNewDesign && (
            <PaymentFrequencySection
              billingInfo={billingInfo}
              billingInfoLoading={loading}
              isRenewalDraft={isRenewalDraft}
              fetchBillingData={fetchBillingData}
              mandatedDownPay={mandatedDownPay}
            />
          )}
          {env.paymentsAndBillingNewDesign && canEditPaymentInfo && (
            <div className={styles.addPaymentSection}>
              <Modal
                variant="full"
                data-testid="billing-add-payment-modal"
                toggle={(open) => (
                  <Button
                    variant="secondary"
                    onClick={open}
                    data-testid="billing-add-payment-button"
                  >
                    <Asset
                      icon={PlusIcon}
                      description="Add payment method"
                      height={12}
                      width={12}
                      className={styles.addIcon}
                    />
                    Add new payment method
                  </Button>
                )}
              >
                {(close) => (
                  <AddPaymentMethodForm
                    fetchBillingData={fetchBillingData}
                    isProcessOne={
                      paymentMethods.some(
                        (method) => method.paymentProcessor === 'PROCESS_ONE'
                      ) ?? false
                    }
                    hasAdyenPaymentMethod={
                      paymentMethods.some(
                        (method) => method.paymentProcessor === 'ADYEN'
                      ) ?? false
                    }
                    invoiceId={billingInfo?.invoiceId}
                    close={close}
                  />
                )}
              </Modal>
            </div>
          )}

          <section className={styles.autoPay}>
            <h5>Auto-Payment Enrollment</h5>
            <div
              className={classNames(styles.toggle, {
                [styles.allowAutoPayDraftUpdate]:
                  allowAutoPayDraftUpdate && !isRenewalDraft,
              })}
              title={autoPayDisabledTooltip}
            >
              <Checkbox
                label=""
                data-testid={'toggle-autopay-switch'}
                name="autopay"
                className={styles.toggleSwitch}
                toggle={true}
                onChange={() => {
                  setAutoPay({
                    data: {
                      invoiceId,
                      autoPay: !hasAutoPay,
                    },
                  })
                    .then(() => {
                      fetchBillingData().then(() => {
                        toast({
                          withSecondaryNav: false,
                          message: 'Autopay has been updated',
                        });
                      });
                    })
                    .catch((error) => {
                      toast({ message: error.message, type: 'error' });
                    });
                }}
                disabled={
                  loading ||
                  updatingAutoPay ||
                  removingPayment ||
                  settingDefaultPaymentMethod ||
                  isDefaultPaymentMethodSweep ||
                  !allowedToUpdateAutoPay
                }
                value={hasAutoPay}
              />
              <div className={styles.autoPayLabel}>
                Currently {hasAutoPay ? '' : 'not '}enrolled in AutoPay
              </div>
              {allowAutoPayDraftUpdate && !isRenewalDraft && (
                <>
                  <div
                    data-testid={'current-due-date-auto-pay'}
                    className={styles.currentDueDate}
                  >
                    {paymentDueDate
                      ? `Due on the ${formatNumberToOrdinal(
                          DateTime.fromISO(paymentDueDate).day
                        )} of each month`
                      : `Due on ${formatDateShort(bindDate || '')}`}
                  </div>
                  <div
                    data-testid={'auto-pay-draft-day'}
                    className={styles.autoPayDraftDay}
                  >
                    {autoPayDraftDay
                      ? `Autopay on the ${formatNumberToOrdinal(
                          autoPayDraftDay
                        )} of each month`
                      : backupAutoPayDayPhrase}
                  </div>
                  {allowedToUpdateAutoPay && (
                    <EditAutoPaySchedule
                      policyId={policyId}
                      currentDueDay={DateTime.fromISO(paymentDueDate).day}
                      currentAutoPayDraftDay={
                        autoPayDraftDay
                          ? autoPayDraftDay
                          : DateTime.fromISO(paymentDueDate).day
                      }
                      disabled={isDefaultPaymentMethodSweep}
                      disabledTitle={autoPayDisabledTooltip}
                      autoPayEligibility={autoPayEligibility}
                      fetchBillingData={fetchBillingData}
                    />
                  )}
                </>
              )}
            </div>
          </section>
        </FlexGridColumn>
      </FlexGrid>
      <ModalFooter></ModalFooter>
    </>
  );
};
